import { startNotificationPrefetch } from '../components/NotificationBell';
import { formatHMFromMinutes } from '../utils/formatHours';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { wantsReturnToProfile, clearReturnToProfile } from '../utils/returnToProfile';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import nlLocale from '@fullcalendar/core/locales/nl';
import frLocale from '@fullcalendar/core/locales/fr';
import SmartStaysLogo from '../components/SmartStaysLogo';
import Avatar from '../components/Avatar';
import NotificationBell from '../components/NotificationBell';
import ProfileMenu from '../components/ProfileMenu';
import { useAppLayout, useNativeShell, scrollPageToTop } from '../utils/nativeApp';
import useStableMonthHeight from '../utils/useStableMonthHeight';
import useMonthSwipe from '../utils/useMonthSwipe';
import AppNav from '../components/AppNav';
import NativeScreenTitle from '../components/NativeScreenTitle';
import API_URL from '../config';
import { openCleaningPdf } from '../utils/openCleaningPdf';
import { getAuthToken, getAuthUser, isImpersonating, stopImpersonating } from '../utils/authSession';
import './CleanerDashboard.css';
import { compareNames } from '../utils/sortNames';

// Afmelden voor een taak kan niet meer binnen 2 weken voor de start (zelfde
// regel als "/cancel-assignment" in de backend).
const CANCEL_LOCK_MS = 14 * 24 * 60 * 60 * 1000;
const isCancelLocked = (task, now) => new Date(task.scheduledAt).getTime() - new Date(now).getTime() < CANCEL_LOCK_MS;

function CleanerDashboard() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [openTasks, setOpenTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
    const [personalCode, setPersonalCode] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [newPersonalCode, setNewPersonalCode] = useState('');
  const [confirmPersonalCode, setConfirmPersonalCode] = useState('');
  const [codeError, setCodeError] = useState('');
  // De kalender is de eerste/standaard tab: puur en enkel de eigen
  // schoonmaakmomenten (rood = nog niet bevestigd, groen = wel bevestigd) -
  // los van "Beschikbare taken" (dat zijn open taken van iedereen, nog niet
  // van mij).
  const [activeTab, setActiveTab] = useState(() => (wantsReturnToProfile() ? 'profile' : 'calendar'));
  useEffect(() => { clearReturnToProfile(); }, []);
  // App-weergave (onderste tabbalk i.p.v. navigatiebalk bovenaan): in de echte
  // iOS/Android-app, en ook in de webapp op een telefoon (smal scherm). Op
  // computer/tablet staat bovenaan de navigatiebalk (AppNav).
  const isNative = useAppLayout();
  useNativeShell(isNative);
  const [nativeUnread, setNativeUnread] = useState(0);
  // Badge bij 'Beschikbare taken': aantal taken waar je je nog echt voor kunt aanmelden (geen reserve-taken).
  const signUpCount = openTasks.length;
  useEffect(() => startNotificationPrefetch(), []);

  useEffect(() => {
    const reset = () => setNativeUnread(0);
    window.addEventListener('smartstays:notifications-read', reset);
    return () => window.removeEventListener('smartstays:notifications-read', reset);
  }, []);
  useEffect(() => {
    if (!isNative) return undefined;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        setNativeUnread(res.data.count || 0);
      } catch (e) { /* stil */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [isNative, activeTab]);
  // Uitklapbaar hamburgermenu i.p.v. de rij van 5 tab-knoppen (Kalender/
  // Beschikbare taken/Mijn taken/Mijn reserves/Mijn code) - zelfde patroon
  // als Admin.js's mobileMenuOpen. "Mijn taken" en "Mijn reserves" staan nu
  // samen achter 1 menu-item (zie render verderop), de rest blijft apart.
  // Live iCal-abonnementslink (webcal://...) van deze schoonmaker - lazy
  // opgehaald/aangemaakt zodra de kalendertab voor het eerst geopend wordt.
  const [calendarFeed, setCalendarFeed] = useState(null);
  const [calendarFeedLoading, setCalendarFeedLoading] = useState(false);
  // Klein opklapmenuutje (3 puntjes naast de maandnaam) i.p.v. de vroegere
  // grote "Kalender op je telefoon"-sectie met 2 volwaardige knoppen onder
  // de kalender - staat in de FullCalendar-header ("right"-toolbarslot),
  // dus op een vaste plek los van de lengte van de maandnaam (die zelf in
  // het "center"-slot staat).
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);
  // Blob-URL van de schoonmaakinstructies-PDF die in de overlay getoond wordt.
  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  // Welke taak in de compacte lijsten (kalenderdag + Beschikbare taken) openstaat.
  const [expandedOpenTaskId, setExpandedOpenTaskId] = useState(null);
  // Kalender: vegen (swipen) naar links/rechts bladert door de maanden, naast
  // de pijltjesknoppen. Raakt enkel een echte, overwegend horizontale veeg aan
  // zodat gewoon naar beneden scrollen gewoon blijft werken.
  const calendarRef = useRef(null);
  // Mijn taken: links taken, rechts reserves (swipen of tikken).
  const [myPane, setMyPane] = useState(0);
  // null = we zijn aan het scrollen/swipen: dan blijven beide lijsten zichtbaar
  // (anders klapt de doellijst dicht tijdens de animatie = flikkeren). Pas
  // zodra het scrollen stilvalt, wordt de niet-actieve lijst ingeklapt.
  const [myPaneSettled, setMyPaneSettled] = useState(0);
  const myPagerRef = useRef(null);
  const myPaneProgrammatic = useRef(false);
  const myPaneTimer = useRef(null);
  const handleMyPagerScroll = (e) => {
    const el = e.currentTarget;
    setMyPaneSettled(null);
    if (!myPaneProgrammatic.current) {
      const idx = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
      setMyPane((prev) => (prev === idx ? prev : idx));
    }
    clearTimeout(myPaneTimer.current);
    myPaneTimer.current = setTimeout(() => {
      myPaneProgrammatic.current = false;
      const idx = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
      setMyPane(idx);
      setMyPaneSettled(idx);
    }, 140);
  };
  // Extra zekerheid naast het scroll-snappen: een duidelijke horizontale veeg wisselt altijd van lijst.
  const myPagerTouch = useRef(null);
  const handleMyPagerTouchStart = (e) => {
    const tt = e.touches[0];
    myPagerTouch.current = { x: tt.clientX, y: tt.clientY };
  };
  const handleMyPagerTouchEnd = (e) => {
    const st = myPagerTouch.current;
    myPagerTouch.current = null;
    if (!st) return;
    const tt = e.changedTouches[0];
    const dx = tt.clientX - st.x;
    const dy = tt.clientY - st.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.4) goToMyPane(dx < 0 ? 1 : 0);
  };
  const goToMyPane = (idx) => {
    const el = myPagerRef.current;
    setMyPane(idx);
    if (!el) return;
    myPaneProgrammatic.current = true;
    setMyPaneSettled(null);
    el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
    clearTimeout(myPaneTimer.current);
    myPaneTimer.current = setTimeout(() => {
      myPaneProgrammatic.current = false;
      setMyPaneSettled(idx);
    }, 450);
  };
  // Maand die de kalender nu toont (bijgehouden via datesSet) + helper om
  // de buurmaanden (links/rechts, aaneensluitend zichtbaar tijdens het vegen)
  // te renderen.
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const shiftMonth = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
  const handleCalendarDatesSet = (arg) => {
    const d = arg.view.currentStart;
    setCalMonth((prev) => (prev.getFullYear() === d.getFullYear() && prev.getMonth() === d.getMonth() ? prev : new Date(d.getFullYear(), d.getMonth(), 1)));
  };
  // Bladeren met de pijltjes of door te vegen: vorige/volgende maand schuift er
  // aaneensluitend in (zie utils/useMonthSwipe.js, ook gebruikt door de
  // verhuurderskalender). FullCalendar zelf bladert pas als de animatie klaar is.
  const swipe = useMonthSwipe({
    monthKey: `${calMonth.getFullYear()}-${calMonth.getMonth()}`,
    onNext: () => calendarRef.current?.getApi().next(),
    onPrev: () => calendarRef.current?.getApi().prev(),
  });
  // Kaarthoogte nooit laten krimpen tussen maanden (zie utils/useStableMonthHeight.js):
  // voorkomt het "flikkeren" onder de agenda en het omhoogspringen na het bladeren.
  useStableMonthHeight(swipe.trackRef, [calMonth, activeTab]);
  const closePdfViewer = () => {
    if (pdfViewerUrl) window.URL.revokeObjectURL(pdfViewerUrl);
    setPdfViewerUrl(null);
  };
  const calendarMenuRef = useRef(null);
  // Eigen voltooide schoonmaakmomenten uit het verleden (zie
  // /api/my-completed-tasks) - enkel gebruikt om ze als aparte, grijs/blauwe
  // stip terug te tonen in de kalendertab, zodat de schoonmaker ook kan
  // terugzien wanneer die effectief heeft schoongemaakt. Los van "myTasks"
  // hierboven, want die mag bewust GEEN voltooide taken blijven tonen (zie
  // commentaar bij /api/my-tasks in backend/index.js).
  const [completedTasks, setCompletedTasks] = useState([]);
  const [completedTasksLoading, setCompletedTasksLoading] = useState(false);
  const [completedTasksLoaded, setCompletedTasksLoaded] = useState(false);
  // Dag die aangeklikt is in de kalender ('YYYY-MM-DD') - de kalendercellen
  // zelf tonen enkel het aantal taken die dag (geen namen/tijden meer, dat
  // paste niet mooi op smalle schermen); het overzicht van die specifieke
  // taken verschijnt hieronder de kalender zodra je een dag aanklikt.
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const codeSavingRef = useRef(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  // Eigen bevestigingsmodaltje i.p.v. window.confirm() - askConfirm() vult
  // confirmState.message/onConfirm, zie de JSX + helper verderop.
  const [confirmState, setConfirmState] = useState(null);
  // Eigen tussenstapje voor "Voeg toe aan mijn agenda" (los van confirmState
  // hierboven, want die heeft vaste "Bevestigen"-knoptekst - hier moet de
  // knop specifiek "Ga door" zeggen).
  const [showCalendarAddConfirm, setShowCalendarAddConfirm] = useState(false);
  // Id van de taak waarvoor een in-/uitklok-verzoek net onderweg is (of null)
  // - zolang dit gezet staat, wordt de bijbehorende knop uitgeschakeld zodat
  // een dubbele klik niet 2x hetzelfde verzoek verstuurt (dat gaf voorheen een
  // verwarrende "je bent al uitgeklokt"-foutmelding bij de 2de klik).
  const [clockActionTaskId, setClockActionTaskId] = useState(null);
  // "Huidig moment", elke 30s ververst - gebruikt voor de in-/uitklok- en
  // afmeld-tijdvenster-checks in "Mijn taken" hieronder. Zonder dit werd
  // "now" enkel herberekend bij een toevallige her-render (bv. na een klik
  // of fetchData), waardoor de inklok-knop uitgeschakeld kon blijven staan
  // als de schoonmaker het tabblad al open had staan van vóór het venster
  // begon - de klok tikte dan wel door, maar de knop "wist" dat niet totdat
  // er toevallig iets anders de pagina deed her-renderen.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNowTick(new Date()), 30 * 1000);
    return () => clearInterval(timer);
  }, []);
  // Ververst "Mijn taken" e.a. stil op de achtergrond, zodat wanneer een
  // collega-schoonmaker in-/uitklokt op een gezamenlijke taak (zie
  // "/clock-in"+"/clock-out" in backend/index.js, dat geldt nu voor de
  // volledige toegewezen ploeg tegelijk), dit ook zichtbaar wordt voor de
  // andere toegewezen schoonmaker(s) zonder dat die zelf de pagina moeten
  // herladen. Bewust gewone polling i.p.v. een WebSocket-verbinding: er is
  // wel al een (ongebruikte) WebSocket-server in de backend, maar niet
  // geverifieerd of poort 3002 op de VPS correct doorgezet/beveiligd is.
  useEffect(() => {
    const poll = setInterval(() => fetchData(true), 20 * 1000);
    return () => clearInterval(poll);
  }, []);
  // Batterijstatus van het slot, ter plaatse te raadplegen per taak (enkel
  // het paneel van 1 taak tegelijk open, bijgehouden via het taak-id).
  const [smartlockPanelTaskId, setSmartlockPanelTaskId] = useState(null);
  const [smartlockStatus, setSmartlockStatus] = useState(null);
  const [smartlockStatusLoading, setSmartlockStatusLoading] = useState(false);
  const [smartlockSyncing, setSmartlockSyncing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    const userData = getAuthUser();

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'SCHOONMAKER') {
      if (parsedUser.role === 'ADMIN') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
      return;
    }

    setUser(parsedUser);
    setPersonalCode(parsedUser.personalCode || '••••••');
    fetchData();
    document.title = 'SmartStays - Schoonmaker Dashboard';
  }, [navigate]);

  // Kalenderlink pas ophalen/aanmaken zodra de kalendertab effectief geopend
  // wordt (en enkel de eerste keer) - niet nodig als de student die tab nooit
  // bezoekt.
  useEffect(() => {
    if (activeTab === 'calendar' && !calendarFeed && !calendarFeedLoading) {
      fetchCalendarFeed();
    }
    if (activeTab === 'calendar' && !completedTasksLoaded && !completedTasksLoading) {
      fetchCompletedTasks();
    }
  }, [activeTab]);

  // Sluit het 3-puntjes-kalendermenu bij een klik erbuiten (zelfde patroon
  // als LanguageSwitcher.js).
  useEffect(() => {
    if (!showCalendarMenu) return undefined;
    const handleClickOutside = (e) => {
      if (calendarMenuRef.current && !calendarMenuRef.current.contains(e.target)) {
        setShowCalendarMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendarMenu]);

  // Zet een datum om naar een 'YYYY-MM-DD'-sleutel in de LOKALE tijdzone van
  // de browser (bewust geen .toISOString(), dat zou naar UTC omzetten en zo
  // soms 1 dag verschuiven) - zelfde manier waarop FullCalendar's eigen
  // "dateClick.dateStr" hieronder de dag van een cel identificeert, zodat
  // beide altijd exact dezelfde sleutel gebruiken.
  const toDateKey = (dateInput) => {
    const d = new Date(dateInput);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Kalender toont per dag enkel het AANTAL taken (geen namen/tijden meer in
  // de cel zelf, dat paste niet mooi op smalle schermen) - dit groepeert
  // daarvoor de eigen taken per dag. Het overzicht van de effectieve taken
  // op een specifieke dag komt pas na het aanklikken van die dag, zie
  // "calendarSelectedDate" hierboven en de render verderop.
  // Vaste volgorde: eerst op starttijd, bij gelijke tijd alfabetisch op
  // woningnaam (en als laatste op id) - anders wisselden taken op hetzelfde
  // moment soms onderling van plaats bij het verversen.
  const compareTasks = (a, b) => {
    const diff = new Date(a.scheduledAt) - new Date(b.scheduledAt);
    if (diff !== 0) return diff;
    const nameDiff = String(a.property_name || '').localeCompare(String(b.property_name || ''), 'nl', { sensitivity: 'base' });
    if (nameDiff !== 0) return nameDiff;
    return String(a.id).localeCompare(String(b.id));
  };

  const tasksByDate = useMemo(() => {
    const map = {};
    [...myTasks].sort(compareTasks).forEach((task) => {
      const key = toDateKey(task.scheduledAt);
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return map;
  }, [myTasks]);

  // Zelfde groepering maar dan voor de nog niet aangenomen (beschikbare)
  // taken, zodat die ook als apart (oranje) bolletje op de kalender kunnen.
  const openTasksByDate = useMemo(() => {
    const map = {};
    [...openTasks].sort(compareTasks).forEach((task) => {
      const key = toDateKey(task.scheduledAt);
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return map;
  }, [openTasks]);

  // Zelfde groepering, maar dan voor de voltooide taken uit het verleden
  // (zie completedTasks hierboven).
  const completedTasksByDate = useMemo(() => {
    const map = {};
    [...completedTasks].sort(compareTasks).forEach((task) => {
      const key = toDateKey(task.scheduledAt);
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return map;
  }, [completedTasks]);

  // Voor de kalenderkleuren (zie render verderop) horen taken waarvoor
  // ikzelf me heb aangemeld - ongeacht of een admin dat al bevestigd heeft -
  // visueel bij elkaar (oranje): dat is voor de schoonmaker "van mij", punt.
  // Enkel taken waar nog niemand op ingetekend heeft (of waar een collega op
  // zit, maar ikzelf niet) blijven "vrij"/blauw. Los van tasksByDate/
  // openTasksByDate hierboven, die blijven ongewijzigd voor de "Mijn taken"/
  // "Beschikbare taken"-tabs.
  const myPendingSignupsByDate = useMemo(() => {
    const map = {};
    [...openTasks].sort(compareTasks).forEach((task) => {
      if (task.cleanerId !== user?.id) return;
      const key = toDateKey(task.scheduledAt);
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return map;
  }, [openTasks, user]);

  const trulyOpenTasksByDate = useMemo(() => {
    const map = {};
    [...openTasks].sort(compareTasks).forEach((task) => {
      if (task.cleanerId === user?.id) return;
      const key = toDateKey(task.scheduledAt);
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return map;
  }, [openTasks, user]);

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const askConfirm = (message, onConfirm) => setConfirmState({ message, onConfirm });

  // "silent" = true bij de achtergrond-polling hieronder: dan NIET de
  // pagina-brede loading-spinner tonen (die zou anders om de paar seconden
  // even alle tabbladen laten verdwijnen) - enkel de eigenlijke data zelf
  // ververst rustig op de achtergrond.
  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = getAuthToken();

      const [openRes, myTasksRes] = await Promise.all([
        axios.get(`${API_URL}/api/cleaning-tasks?status=OPEN,ASSIGNED&withDetails=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/my-tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Enkel open taken (waar nog niemand op staat): daar kun je je voor aanmelden.
      const visibleOpenTasks = openRes.data.filter((task) => task.status === 'OPEN' || task.status === 'ASSIGNED');

      setOpenTasks(visibleOpenTasks);
      setMyTasks(myTasksRes.data);
    } catch (error) {
      console.error('Fout bij ophalen data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (isImpersonating()) {
          // Enkel de impersonatie-sessie van dit tabblad beëindigen - niet de
          // echte, gedeelde sessie van de admin (localStorage) aanraken.
          stopImpersonating();
          window.location.href = '/admin';
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        }
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Live iCal-abonnementslink ophalen (wordt bij de eerste aanvraag serverzijde
  // aangemaakt als die nog niet bestaat) - zie /api/users/me/calendar-feed.
  const fetchCalendarFeed = async () => {
    setCalendarFeedLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/users/me/calendar-feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarFeed(response.data);
    } catch (error) {
      showToast(t('cleaner.calendarFeedError'), 'error');
    } finally {
      setCalendarFeedLoading(false);
    }
  };

  // Geschiedenis van voltooide schoonmaakmomenten - enkel opgehaald zodra de
  // kalendertab voor het eerst geopend wordt (en enkel de eerste keer), net
  // zoals de kalenderlink hierboven.
  const fetchCompletedTasks = async () => {
    setCompletedTasksLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/my-completed-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedTasks(response.data);
      setCompletedTasksLoaded(true);
    } catch (error) {
      console.error('Fout bij ophalen voltooide taken:', error);
    } finally {
      setCompletedTasksLoading(false);
    }
  };

  const handleCopyCalendarLink = async () => {
    if (!calendarFeed) return;
    try {
      await navigator.clipboard.writeText(calendarFeed.httpsUrl);
      showToast(t('cleaner.calendarLinkCopied'), 'success');
    } catch (error) {
      showToast(t('cleaner.calendarCopyError'), 'error');
    }
  };

  const handleAssignTask = async (taskId) => {
    askConfirm(t('cleaner.confirmAssign'), async () => {
      try {
        const token = getAuthToken();
        await axios.post(`${API_URL}/api/cleaning-tasks/${taskId}/assign`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast(t('cleaner.taskAssigned'), 'success');
        // Stil verversen (geen laad-scherm): anders verdwijnt de hele lijst heel
        // even en springt het scherm naar boven.
        fetchData(true);
      } catch (error) {
        showToast(error.response?.data?.error || t('cleaner.assignError'), 'error');
      }
    });
  };

  const handleCancelAssignment = async (taskId) => {
    askConfirm(t('cleaner.confirmCancelAssignment'), async () => {
      try {
        const token = getAuthToken();
        await axios.post(`${API_URL}/api/cleaning-tasks/${taskId}/cancel-assignment`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast(t('cleaner.cancelledAssignment'), 'success');
        fetchData(true);
      } catch (error) {
        showToast(error.response?.data?.error || t('cleaner.cancelError'), 'error');
      }
    });
  };

  // In-/uitklokken: elke toegewezen schoonmaker (hoofd of co-schoonmaker)
  // doet dit voor zichzelf, enkel mogelijk binnen de voorziene tijd van de
  // taak. Zodra de laatste toegewezen schoonmaker uitklokt, markeert de
  // backend de taak automatisch als voltooid (zie "/clock-out" in
  // backend/index.js) - er is dus bewust geen aparte "Markeer als
  // voltooid"-knop/actie meer nodig hier.
  const handleClockIn = (taskId) => {
    if (clockActionTaskId) return;
    askConfirm(t('cleaner.confirmClockIn'), async () => {
      setClockActionTaskId(taskId);
      try {
        const token = getAuthToken();
        await axios.post(`${API_URL}/api/cleaning-tasks/${taskId}/clock-in`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast(t('cleaner.clockedIn'), 'success');
        fetchData(true);
      } catch (error) {
        showToast(error.response?.data?.error || t('cleaner.clockInError'), 'error');
      } finally {
        setClockActionTaskId(null);
      }
    });
  };

  const handleClockOut = (taskId) => {
    if (clockActionTaskId) return;
    askConfirm(t('cleaner.confirmClockOut'), async () => {
      setClockActionTaskId(taskId);
      try {
        const token = getAuthToken();
        const response = await axios.post(`${API_URL}/api/cleaning-tasks/${taskId}/clock-out`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast(
          response.data.task?.status === 'COMPLETED' ? t('cleaner.taskCompleted') : t('cleaner.clockedOut'),
          'success'
        );
        fetchData(true);
      } catch (error) {
        showToast(error.response?.data?.error || t('cleaner.clockOutError'), 'error');
      } finally {
        setClockActionTaskId(null);
      }
    });
  };

  // Opent de schoonmaakinstructies-PDF van deze woning (geüpload door admin
  // of verhuurder) in een nieuw tabblad. Puur informatief - staat los van
  // "voltooid" (zie handleCompleteTask hierboven).
  const handleViewCleaningPdf = async (task) => {
    try {
      await openCleaningPdf(task.propertyId);
    } catch (error) {
      showToast(t('cleaner.cleaningPdfLoadError'), 'error');
    }
  };

  // Toont/verbergt het batterijpaneel voor 1 taak. Haalt de status pas op
  // bij het openen, niet automatisch voor elke taak in de lijst.
  const toggleSmartlockPanel = (task) => {
    if (smartlockPanelTaskId === task.id) {
      setSmartlockPanelTaskId(null);
      setSmartlockStatus(null);
      return;
    }
    setSmartlockPanelTaskId(task.id);
    setSmartlockStatus(null);
    fetchSmartlockStatus(task.propertyId);
  };

  const fetchSmartlockStatus = async (propertyId) => {
    try {
      setSmartlockStatusLoading(true);
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/properties/${propertyId}/smartlock-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartlockStatus(response.data);
    } catch (error) {
      console.error('Fout bij ophalen smartlock-status:', error);
      setSmartlockStatus(null);
    } finally {
      setSmartlockStatusLoading(false);
    }
  };

  const handleSyncSmartlock = async (propertyId) => {
    setSmartlockSyncing(true);
    try {
      const token = getAuthToken();
      const response = await axios.post(`${API_URL}/api/properties/${propertyId}/smartlock-sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartlockStatus(response.data);
    } catch (error) {
      console.error('Fout bij synchroniseren smartlock-status:', error);
    } finally {
      setSmartlockSyncing(false);
    }
  };

  const validateCode = (code) => {
    // Zelfde regel als de backend (nukiService.isValidKeypadCode): exact 6
    // cijfers, enkel 1-9 (geen 0), en niet startend met "12".
    if (!/^[1-9]{6}$/.test(code) || code.startsWith('12')) {
      return t('cleaner.codeMustBe6');
    }
    return null;
  };

  const handleChangePersonalCode = async () => {
    const error = validateCode(newPersonalCode);
    if (error) {
      setCodeError(error);
      return;
    }

    if (newPersonalCode !== confirmPersonalCode) {
      setCodeError(t('cleaner.codesNoMatch'));
      return;
    }

    // Geen pagina-brede laadstatus (zou het scherm laten verspringen); enkel
    // dubbel indienen voorkomen.
    if (codeSavingRef.current) return;
    codeSavingRef.current = true;
    try {
      const token = getAuthToken();
      // Dit endpoint geeft enkel { success, message } terug, geen bijgewerkte
      // user - we werken de lokale user dus zelf bij met de net bevestigde code.
      await axios.put(`${API_URL}/api/users/me/personal-code`,
        { personalCode: newPersonalCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUser = { ...user, personalCode: newPersonalCode };
      // Zie Dashboard.js: niet naar localStorage schrijven tijdens impersonatie,
      // anders overschrijft dit de echte admin-sessie.
      if (!isImpersonating()) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      setUser(updatedUser);
      setPersonalCode(newPersonalCode);

      setShowCodeModal(false);
      setNewPersonalCode('');
      setConfirmPersonalCode('');
      setCodeError('');
      showToast(t('cleaner.codeChanged'), 'success');
    } catch (error) {
      setCodeError(error.response?.data?.error || t('cleaner.codeChangeError'));
    } finally {
      codeSavingRef.current = false;
    }
  };

  const handleLogout = () => {
    // Als dit tabblad enkel een impersonatie-sessie was ("Inloggen als" vanuit
    // Admin.js), keert dit tabblad terug naar de echte, ongewijzigde
    // admin-sessie i.p.v. een volledige uitlog.
    if (isImpersonating()) {
      stopImpersonating();
      window.location.href = '/admin';
      return;
    }

    const isNative = localStorage.getItem('isNativeApp') === 'true';
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    if (isNative) {
      window.location.href = '/splash';
    } else {
      window.location.href = '/';
    }
  };

  const formatDateOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short'
    });
  };

  const formatTimeOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  };

  // Toont steeds het beginuur-einduur van de schoonmaak (i.p.v. enkel de
  // startdatum/-tijd zoals voorheen) - bij een periode van meerdere dagen
  // staat de datum erbij vermeld op beide uiteinden.
  const formatCleaningPeriod = (task) => {
    const startDate = new Date(task.scheduledAt);
    const endDate = task.cleaningEnd ? new Date(task.cleaningEnd) : startDate;
    const isPeriod = startDate.toDateString() !== endDate.toDateString();
    if (isPeriod) {
      return <>{formatDateOnly(task.scheduledAt)} {formatTimeOnly(task.scheduledAt)} - {formatDateOnly(task.cleaningEnd)} {formatTimeOnly(task.cleaningEnd)}</>;
    }
    return <>{formatDateOnly(task.scheduledAt)}, {formatTimeOnly(task.scheduledAt)} - {formatTimeOnly(task.cleaningEnd)}</>;
  };

  // Zelfde als hierboven, maar zonder de datum erbij - gebruikt in
  // "Beschikbare taken"/"Mijn taken" waar de taken per datum gegroepeerd
  // worden (zie tasksByDate/openTasksByDate hierboven) met de datum als 1
  // gemeenschappelijke, opvallende kop boven de groep i.p.v. klein herhaald
  // op elke afzonderlijke taak. Bij een periode die toch meerdere dagen
  // overspant, blijven beide datums wel vermeld - de groepskop toont enkel
  // de startdatum, dus de einddatum zou anders verloren gaan.
  const formatCleaningTimeRange = (task) => {
    const startDate = new Date(task.scheduledAt);
    const endDate = task.cleaningEnd ? new Date(task.cleaningEnd) : startDate;
    const isPeriod = startDate.toDateString() !== endDate.toDateString();
    if (isPeriod) {
      return formatCleaningPeriod(task);
    }
    return <>{formatTimeOnly(task.scheduledAt)} - {formatTimeOnly(task.cleaningEnd)}</>;
  };

  // Kop boven een groep taken die dezelfde datum delen (zie tasksByDate/
  // openTasksByDate) - dezelfde locale-keuze als het kalender-dagoverzicht
  // elders in dit bestand, voor een consistente weergave in alle 3 talen.
  const formatDateHeading = (dateKey) => {
    const date = new Date(`${dateKey}T00:00:00`);
    const locale = i18n.language?.startsWith('fr') ? 'fr-BE' : i18n.language?.startsWith('en') ? 'en-GB' : 'nl-BE';
    const label = date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  // "Duur" is het aantal uur dat deze schoonmaker effectief moet werken
  // (worked_minutes, al gedeeld door het aantal toegewezen schoonmakers -
  // zie backend CLEANING_TASK_DETAILS_SELECT) - niet de volledige voorziene
  // tijdsspanne van de taak zelf. Zelfde weergave als bij Admin.js.
  const formatDurationMinutes = (minutes) => {
    if (minutes === null || minutes === undefined) return '';
    return `${formatHMFromMinutes(minutes)} ${t('cleaner.hoursUnit', { count: 2 })}`;
  };

  if (!user) return <div aria-busy="true" />;

  // Compacte taakrij voor open/aangemelde taken: bovenaan enkel naam + tijd en
  // duur + aantal schoonmakers; adres, wie er al op staat, e.d. pas na het
  // aanklikken, samen met 3 knoppen naast elkaar (Annuleren rood, Reserve
  // oranje, Aanmelden blauw - grijs zodra de actie niet (meer) kan).
  const taskStripeClass = (task, uid) => {
    if (['COMPLETED', 'INVOICED'].includes(task.status)) return 'ct-stripe-completed';
    if ([task.cleanerId, task.cleanerId2, task.cleanerId3].includes(uid)) return 'ct-stripe-mine';
    return 'ct-stripe-available';
  };

  // Taak openklappen / dichtklappen: er staat er altijd maximaal 1 open (een
  // andere openen sluit de vorige). Na het openen scrollt de kaart netjes in
  // beeld (anders blijft de onderkant achter de tabbalk hangen).
  const toggleCompactTask = (id) => {
    const opening = expandedOpenTaskId !== id;
    setExpandedOpenTaskId(opening ? id : null);
    if (opening && typeof window !== 'undefined') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        const el = document.querySelector('.compact-task.expanded');
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }));
    }
  };

  // Aantal schoonmakers dat zich al aangemeld heeft t.o.v. het minimum dat
  // nodig is: 0 = rood, minder dan nodig = oranje, genoeg = groen (zelfde
  // kleuren als de bezetting in het adminpaneel).
  const getSignupCount = (task) => [task.cleanerId, task.cleanerId2, task.cleanerId3].filter(Boolean).length;
  // Bezetting van een taak: 'none' (rood) = nog niemand aangemeld, 'partial'
  // (oranje) = minimum nog niet bereikt, 'full' (blauw) = minimum bereikt.
  // Zelfde kleuren in de lijst (badge) en op de kalender (bolletjes).
  const getSignupLevel = (task) => {
    const done = getSignupCount(task);
    const min = Math.max(Number(task.expected_cleaner_count) || 1, 1);
    return done === 0 ? 'none' : done < min ? 'partial' : 'full';
  };
  const renderSignupBadge = (task) => {
    const done = getSignupCount(task);
    const min = Math.max(Number(task.expected_cleaner_count) || 1, 1);
    // Zonder apart maximum (oudere taken/woningen) is het maximum gewoon het minimum.
    const total = Math.max(Number(task.max_cleaner_count) || min, min);
    const level = getSignupLevel(task);
    return (
      <span
        className={`compact-task-signup ${level}`}
        title={`${t('cleaner.cleanerCountLabel')}: ${min} · ${t('cleaner.maxCleanerCountLabel')}: ${total}`}
      >
        {t('cleaner.signedUpOf', { done, total })}
      </span>
    );
  };

  const renderCompactTask = (task, opts = {}) => {
    const readOnly = !!opts.readOnly;
    const isMine = readOnly || [task.cleanerId, task.cleanerId2, task.cleanerId3].includes(user.id);
    const hasStarted = nowTick >= new Date(task.scheduledAt);
    const expanded = expandedOpenTaskId === task.id;
    const canSignUp = task.status === 'OPEN' || (task.status === 'ASSIGNED' && !isMine);
    const canCancel = isMine && !isCancelLocked(task, nowTick);
    const assignedCleaners = [
      { name: task.cleaner_name, photo: task.cleaner_photo_url },
      { name: task.cleaner2_name, photo: task.cleaner2_photo_url },
      { name: task.cleaner3_name, photo: task.cleaner3_photo_url },
    ].filter((c) => c.name).sort((a, b) => compareNames(a.name, b.name));
    return (
      <div key={task.id} className={`compact-task ${expanded ? 'expanded' : ''} ${taskStripeClass(task, user.id)}`}>
        <div
          className="compact-task-summary"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => toggleCompactTask(task.id)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCompactTask(task.id); } }}
        >
          {task.property_photo_url && <img src={`${API_URL}${task.property_photo_url}`} alt="" className="compact-task-photo" />}
          <div className="compact-task-summary-body">
          <div className="compact-task-line">
            <span className="compact-task-name">
              {task.property_name}
            </span>
            <span className="compact-task-time">
              {formatTimeOnly(task.scheduledAt)}{task.cleaningEnd ? ` - ${formatTimeOnly(task.cleaningEnd)}` : ''}
            </span>
          </div>
          <div className="compact-task-line compact-task-meta">
            <span>{t('cleaner.duration')}: {formatDurationMinutes(task.total_work_minutes)}</span>
            {renderSignupBadge(task)}
          </div>
          </div>
          <span className="compact-task-chevron" aria-hidden="true" />
        </div>
        {expanded && (
          <div className="compact-task-details">
            <p><strong>{t('common.address')}:</strong> {task.address || t('cleaner.noAddress')}</p>
            {isMine && task.notes && <p><strong>{t('cleaner.notes')}:</strong> {task.notes}</p>}
            {readOnly && task.extra_info && <p style={{ whiteSpace: 'pre-line' }}><strong>{t('admin.extraInfoLabel')}:</strong> {task.extra_info}</p>}
            {assignedCleaners.length > 0 && (
              <div className="compact-task-people">
                <strong>{t('cleaner.assignedTo')}:</strong>
                {assignedCleaners.map((c) => (
                  <div key={c.name} className="compact-task-person">
                    <Avatar url={c.photo} name={c.name} size={32} />
                    <span>{c.name}</span>
                  </div>
                ))}
              </div>
            )}
            {!readOnly && (
            <div className="compact-task-actions">
              {/* Binnen 2 weken voor de taak kan je je niet meer afmelden: dan
                  staat de knop er niet. */}
              {canCancel && (
                <button
                  type="button"
                  className="ct-btn ct-btn-cancel"
                  onClick={() => { setExpandedOpenTaskId(null); handleCancelAssignment(task.id); }}
                >
                  {t('common.cancel')}
                </button>
              )}
              <button
                type="button"
                className="ct-btn ct-btn-assign"
                disabled={!canSignUp}
                onClick={() => { setExpandedOpenTaskId(null); handleAssignTask(task.id); }}
              >
                {t('cleaner.assign')}
              </button>
            </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMonthCalendar = (extra) => (
            <FullCalendar
              {...extra}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              dayHeaderFormat={{ weekday: 'narrow' }}
              fixedWeekCount={true}
              showNonCurrentDates={false}
              headerToolbar={{ left: '', center: 'title', right: 'calendarMenuBtn' }}
              customButtons={{
                // 3 puntjes i.p.v. de vroegere grote "Kalender op je
                // telefoon"-sectie onder de kalender - zit in de "right"-
                // toolbarslot, dus op een vaste plek t.o.v. de rand van de
                // kalender, los van hoe lang de maandnaam (het "center"-
                // slot) toevallig is.
                calendarMenuBtn: {
                  text: '⋮',
                  click: () => setShowCalendarMenu((prev) => !prev),
                },
              }}
              height="auto"
              locale={i18n.language?.startsWith('fr') ? frLocale : i18n.language?.startsWith('en') ? 'en' : nlLocale}
              firstDay={1}
              dateClick={(info) => setCalendarSelectedDate((prev) => (prev === info.dateStr ? null : info.dateStr))}
              dayCellClassNames={(args) => (toDateKey(args.date) === calendarSelectedDate ? ['calendar-day-selected'] : [])}
              dayCellContent={(args) => {
                const key = toDateKey(args.date);
                // Voorheen 1 gecombineerde oranje badge (myPendingSignupsByDate +
                // tasksByDate samen via getAssignedItemsForDate) - nu 2 aparte
                // tinten: lichtoranje voor een aanmelding die nog niet door een
                // admin bevestigd is ("Aangevraagd"), donkeroranje zodra die wel
                // bevestigd is ("Toegewezen"). getAssignedItemsForDate blijft wel
                // bestaan/gebruikt voor het dagoverzicht-sorteren hieronder.
                const dayRequestedTasks = myPendingSignupsByDate[key] || [];
                const dayAssignedTasks = tasksByDate[key] || [];
                const dayOpenTasks = trulyOpenTasksByDate[key] || [];
                const dayCompletedTasks = completedTasksByDate[key] || [];
                // Beschikbare taken per bezetting: rood (niemand), oranje
                // (minimum niet bereikt), blauw (minimum bereikt).
                const dayOpenByLevel = { none: 0, partial: 0, full: 0 };
                dayOpenTasks.forEach((task) => { dayOpenByLevel[getSignupLevel(task)] += 1; });
                return (
                  <div className="calendar-day-cell-content">
                    <span className="calendar-day-number">{args.dayNumberText}</span>
                    {(dayRequestedTasks.length > 0 || dayAssignedTasks.length > 0 || dayOpenTasks.length > 0 || dayCompletedTasks.length > 0) && (
                      <span className="calendar-day-badges">
                        {dayRequestedTasks.length > 0 && (
                          <span className="calendar-day-count requested">{dayRequestedTasks.length}</span>
                        )}
                        {dayAssignedTasks.length > 0 && (
                          <span className="calendar-day-count confirmed">{dayAssignedTasks.length}</span>
                        )}
                        {['none', 'partial', 'full'].map((level) => (
                          dayOpenByLevel[level] > 0 && (
                            <span key={level} className={`calendar-day-count available available-${level}`}>{dayOpenByLevel[level]}</span>
                          )
                        ))}
                        {dayCompletedTasks.length > 0 && (
                          <span className="calendar-day-count completed">{dayCompletedTasks.length}</span>
                        )}
                      </span>
                    )}
                  </div>
                );
              }}
            />
  );

  const handleNotificationNavigate = (n) => {
    if (n.type === 'TASK_OPEN' || n.type === 'TASK_REPLACED') {
      setActiveTab('available');
    } else if (n.type === 'TASK_CONFIRMED' || n.type === 'TASK_REMINDER') {
      setActiveTab('my-tasks');
    }
  };

  // Titel bovenaan in de app (zonder aantallen): altijd gecentreerd op dezelfde plek.
  const stripCount = (s) => s.replace(/\s*\(\d+\)\s*$/, '');
  const nativeTitle = !isNative ? null
    : activeTab === 'calendar' ? t('cleaner.tabCalendar')
    : activeTab === 'available' ? stripCount(t('cleaner.tabAvailable', { count: openTasks.length }))
    : (activeTab === 'my-tasks' || activeTab === 'reserves') ? stripCount(t('cleaner.tabMyTasks', { count: myTasks.length }))
    : activeTab === 'my-code' ? t('cleaner.tabMyCode')
    : activeTab === 'notifications' ? t('notifications.title')
    : activeTab === 'profile' ? t('profileMenu.title')
    : null;

  return (
    <div className={`cleaner-container ${isNative ? 'cleaner-container-native' : ''}`}>
      {isNative ? (
        <nav className="cleaner-nav">
          <div className="nav-brand">
            <SmartStaysLogo className="nav-logo" />
          </div>
          <NativeScreenTitle title={nativeTitle} />
        </nav>
      ) : (
        <AppNav
          items={[
            { key: 'calendar', label: t('cleaner.tabCalendar'), active: activeTab === 'calendar', onClick: () => setActiveTab('calendar') },
            { key: 'available', label: t('cleaner.tabAvailable', { count: openTasks.length }), active: activeTab === 'available', onClick: () => setActiveTab('available') },
            { key: 'my-tasks', label: t('cleaner.tabMyTasks', { count: myTasks.length }), active: activeTab === 'my-tasks' || activeTab === 'reserves', onClick: () => { setActiveTab('my-tasks'); scrollPageToTop('smooth'); } },
          ]}
          right={(
            <>
              <NotificationBell onNotificationClick={handleNotificationNavigate} />
              <ProfileMenu user={user} onLogout={handleLogout} onOpenMyCode={() => { setActiveTab('my-code'); scrollPageToTop(); }} />
            </>
          )}
        />
      )}

      {toast.show && (
        <div className={`toast-message ${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Eigen bevestigingsmodaltje i.p.v. window.confirm() - askConfirm()
          hierboven vult confirmState.message/onConfirm; de knop voert
          onConfirm uit en sluit het modaltje. */}
      {pdfViewerUrl && (
        <div className="pdf-viewer-overlay">
          <div className="pdf-viewer-bar">
            <a href={pdfViewerUrl} target="_blank" rel="noopener noreferrer" className="pdf-viewer-open">
              {t('cleaner.cleaningPdfOpenNewTab')}
            </a>
            <button type="button" className="modal-close" onClick={closePdfViewer} aria-label={t('common.close')}>✖</button>
          </div>
          <iframe src={pdfViewerUrl} title="PDF" className="pdf-viewer-frame" />
        </div>
      )}

      {confirmState && (
        <div className="modal-overlay" onClick={() => setConfirmState(null)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('common.confirm')}</h2>
              <button className="modal-close" onClick={() => setConfirmState(null)}>✖</button>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-line' }}>{confirmState.message}</p>
            </div>
            <div className="modal-buttons">
              <button type="button" className="cancel-btn" onClick={() => setConfirmState(null)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="submit-btn"
                onClick={() => { const { onConfirm } = confirmState; setConfirmState(null); onConfirm(); }}
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="cleaner-content">
        {/* Op computer/tablet toont de navigatiebalk al welk onderdeel actief is;
            de titel staat dan enkel nog bij "Mijn code" (geen actieve knop). */}
        {(isNative || activeTab === 'my-code') && (
          <div className="cleaner-header">
            {/* Welk item nu actief is - de knop zelf staat sinds kort in de
                navbar (zelfde plek als bij de verhuurder), maar dit label
                blijft hier staan zodat je nog steeds in 1 oogopslag ziet welk
                onderdeel je aan het bekijken bent. */}
            <span className="cleaner-menu-current">
              {activeTab === 'calendar' && t('cleaner.tabCalendar')}
              {activeTab === 'available' && t('cleaner.tabAvailable', { count: openTasks.length })}
              {(activeTab === 'my-tasks' || activeTab === 'reserves') && t('cleaner.tabMyTasks', { count: myTasks.length })}
              {activeTab === 'my-code' && t('cleaner.tabMyCode')}
              {isNative && activeTab === 'profile' && t('profileMenu.title')}
            </span>
          </div>
        )}

        {loading && <div className="loading" aria-busy="true" />}

        {isNative && activeTab === 'notifications' && (
          <NotificationBell screen onNotificationClick={handleNotificationNavigate} />
        )}
        {isNative && activeTab === 'profile' && (
          <ProfileMenu screen user={user} onLogout={handleLogout} onOpenMyCode={() => { setActiveTab('my-code'); scrollPageToTop(); }} />
        )}

        {!loading && activeTab === 'calendar' && (
          <div className="cleaner-calendar-section">
            {/* De kalender blijft altijd staan, ook zonder taken (dan gewoon
                leeg) - voorheen verdween ze volledig achter de "geen taken"-
                tekst, wat verwarrend was omdat je dan niet meer kon zien
                welke maand je bekeek of naar een andere maand kon bladeren.
                De "geen taken"-tekst zelf tonen we niet meer (op expliciet
                verzoek) - een lege kalender spreekt voor zich. */}
            <div className="calendar-nav-row">
              <button type="button" className="calendar-nav-btn" onClick={() => swipe.slide(-1)} aria-label={t('calendar.previous')}>&#8249;</button>
              <button type="button" className="calendar-nav-btn" onClick={() => swipe.slide(1)} aria-label={t('calendar.next')}>&#8250;</button>
              <button type="button" className="calendar-nav-today" onClick={() => calendarRef.current?.getApi().today()}>{t('calendar.today')}</button>
            </div>
            <div
              className="cleaner-calendar-card"
              ref={swipe.cardRef}
              {...swipe.handlers}
            >
            <div className="cleaner-calendar-track" ref={swipe.trackRef}>
              <div className="cleaner-calendar-pane cleaner-calendar-pane-prev" key={`p-${calMonth.getFullYear()}-${calMonth.getMonth()}`} aria-hidden="true">
                {renderMonthCalendar({ initialDate: shiftMonth(calMonth, -1) })}
              </div>
              <div className="cleaner-calendar-pane cleaner-calendar-pane-current">
                {renderMonthCalendar({ ref: calendarRef, initialDate: calMonth, datesSet: handleCalendarDatesSet })}
              </div>
              <div className="cleaner-calendar-pane cleaner-calendar-pane-next" key={`n-${calMonth.getFullYear()}-${calMonth.getMonth()}`} aria-hidden="true">
                {renderMonthCalendar({ initialDate: shiftMonth(calMonth, 1) })}
              </div>
            </div>
            </div>
            {calendarSelectedDate && (
              <div className="calendar-day-overview">
                <h4>
                  {new Date(calendarSelectedDate).toLocaleDateString(i18n.language?.startsWith('fr') ? 'fr-BE' : i18n.language?.startsWith('en') ? 'en-GB' : 'nl-BE', {
                    weekday: 'long', day: 'numeric', month: 'long',
                  })}
                </h4>
                {(tasksByDate[calendarSelectedDate] || []).length === 0 && (openTasksByDate[calendarSelectedDate] || []).length === 0 && (completedTasksByDate[calendarSelectedDate] || []).length === 0 ? (
                  <p className="no-tasks">{t('cleaner.calendarNoTasksThisDay')}</p>
                ) : (
                  <>
                    {(completedTasksByDate[calendarSelectedDate] || []).length > 0 && (
                      <div className="calendar-day-overview-group">
                        <div className="calendar-day-overview-group-label completed">{t('cleaner.calendarLegendCompleted')}</div>
                        <div className="calendar-day-overview-list">
                          {(completedTasksByDate[calendarSelectedDate] || [])
                            .slice()
                            .sort(compareTasks)
                            .map((task) => renderCompactTask(task, { readOnly: true }))}
                        </div>
                      </div>
                    )}

                    {/* Voorheen 1 gecombineerde groep ("Aangenomen", via
                        getAssignedItemsForDate) voor zowel nog-niet-bevestigde
                        aanmeldingen als effectief bevestigde toewijzingen - nu 2
                        aparte groepen met elk hun eigen kleur/label, zelfde
                        tinten als de legende hierboven (lichtoranje/donkeroranje).
                        getAssignedItemsForDate zelf blijft ongewijzigd bestaan
                        (nog gebruikt voor de kalendercel-badges verderop). */}
                    {(myPendingSignupsByDate[calendarSelectedDate] || []).length > 0 && (
                      <div className="calendar-day-overview-group">
                        <div className="calendar-day-overview-group-label requested">{t('cleaner.calendarLegendRequested')}</div>
                        <div className="calendar-day-overview-list">
                          {(myPendingSignupsByDate[calendarSelectedDate] || [])
                            .slice()
                            .sort(compareTasks)
                            .map((task) => renderCompactTask(task))}
                        </div>
                      </div>
                    )}

                    {(tasksByDate[calendarSelectedDate] || []).length > 0 && (
                      <div className="calendar-day-overview-group">
                        <div className="calendar-day-overview-group-label mine">{t('cleaner.calendarLegendAssigned')}</div>
                        <div className="calendar-day-overview-list">
                          {(tasksByDate[calendarSelectedDate] || [])
                            .slice()
                            .sort(compareTasks)
                            .map((task) => renderCompactTask(task, { readOnly: true }))}
                        </div>
                      </div>
                    )}

                    {(trulyOpenTasksByDate[calendarSelectedDate] || []).length > 0 && (
                      <div className="calendar-day-overview-group">
                        <div className="calendar-day-overview-group-label available">{t('cleaner.available')}</div>
                        <div className="calendar-day-overview-list">
                          {(trulyOpenTasksByDate[calendarSelectedDate] || [])
                            .slice()
                            .sort(compareTasks)
                            .map((task) => renderCompactTask(task))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Klein opklapmenuutje i.p.v. de vroegere grote "Kalender op je
                telefoon"-sectie hieronder - het "⋮"-knopje zelf zit nu in de
                FullCalendar-toolbar (zie customButtons/headerToolbar
                hierboven), dit is enkel nog de bijhorende dropdown-lijst. */}
            {showCalendarMenu && (
              <div className="calendar-menu-dropdown" ref={calendarMenuRef}>
                <button
                  type="button"
                  className="calendar-menu-dropdown-item"
                  onClick={() => {
                    setShowCalendarMenu(false);
                    if (calendarFeed) {
                      setShowCalendarAddConfirm(true);
                    } else {
                      fetchCalendarFeed();
                    }
                  }}
                >
                  {t('cleaner.calendarAddButton')}
                </button>
                <button
                  type="button"
                  className="calendar-menu-dropdown-item"
                  onClick={() => { setShowCalendarMenu(false); handleCopyCalendarLink(); }}
                >
                  {t('cleaner.calendarCopyLink')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Eigen tussenstapje bij "Voeg toe aan mijn agenda": toont pas de
            update-frequentie-tip (calendarUpdateNote) NA klikken, i.p.v.
            altijd zichtbaar onder de kalender. "Ga door" is een echte
            <a href="webcal://..."> zodat de telefoon dit nog steeds als
            agenda-abonnement herkent (net als de knop hiervoor). */}
        {showCalendarAddConfirm && calendarFeed && (
          <div className="modal-overlay" onClick={() => setShowCalendarAddConfirm(false)}>
            <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('cleaner.calendarSubscribeTitle')}</h2>
                <button className="modal-close" onClick={() => setShowCalendarAddConfirm(false)}>✖</button>
              </div>
              <div className="modal-body">
                <p style={{ whiteSpace: 'pre-line' }}>{t('cleaner.calendarUpdateNote')}</p>
              </div>
              <div className="modal-buttons">
                <button type="button" className="cancel-btn" onClick={() => setShowCalendarAddConfirm(false)}>
                  {t('common.cancel')}
                </button>
                <a
                  href={calendarFeed.webcalUrl}
                  className="submit-btn"
                  onClick={() => setShowCalendarAddConfirm(false)}
                >
                  {t('cleaner.calendarContinue')}
                </a>
              </div>
            </div>
          </div>
        )}

        {!loading && activeTab === 'available' && (
          <div className="tasks-date-groups">
            {openTasks.length === 0 ? (
              <div className="no-tasks">{t('cleaner.noAvailableTasks')}</div>
            ) : (
              // Per datum gegroepeerd (zie openTasksByDate hierboven, al
              // gebruikt voor de kalendertab) - de datum staat 1x groot/vet
              // bovenaan de groep i.p.v. klein herhaald op elke taak.
              Object.entries(openTasksByDate).map(([dateKey, tasksForDate]) => {
                if (tasksForDate.length === 0) return null;
                return (
                <div key={dateKey} className="tasks-date-group">
                  <h3 className="tasks-date-heading">{formatDateHeading(dateKey)}</h3>
                  <div className="tasks-grid">
                    {tasksForDate.map(task => renderCompactTask(task))}
                  </div>
                </div>
                );
              })
            )}
          </div>
        )}

        {/* "Mijn taken" en "Mijn reserves" (voorheen 2 losse tabs) staan nu
            samen achter het "Mijn taken"-item in het hamburgermenu - beide
            secties tonen we hieronder na elkaar, elk met een eigen titel. */}
        {!loading && activeTab === 'my-tasks' && (
          <div>
        {!loading && activeTab === 'my-tasks' && (
          <div className="tasks-date-groups">
            {myTasks.length === 0 ? (
              <div className="no-tasks">{t('cleaner.noMyTasks')}</div>
            ) : (
              // Per datum gegroepeerd (zie tasksByDate hierboven, al gebruikt
              // voor de kalendertab) - de datum staat 1x groot/vet bovenaan
              // de groep i.p.v. klein herhaald op elke taak.
              Object.entries(tasksByDate).map(([dateKey, tasksForDate]) => (
              <div key={dateKey} className="tasks-date-group">
                <h3 className="tasks-date-heading">{formatDateHeading(dateKey)}</h3>
                <div className="tasks-grid">
                {tasksForDate.map(task => {
                const startDate = new Date(task.scheduledAt);
                const endDate = task.cleaningEnd ? new Date(task.cleaningEnd) : startDate;
                const statusInfo = {
                  PENDING_CONFIRMATION: { cls: 'open', label: t('cleaner.statusPending') },
                  // "WITHDRAWN": deze taak had een schoonmaker die zich heeft
                  // teruggetrokken en waar ik als reserve automatisch kandidaat
                  // voor geworden ben - voor mij voelt dit hetzelfde aan als een
                  // gewone kandidatuur die nog bevestigd moet worden.
                  WITHDRAWN: { cls: 'open', label: t('cleaner.statusPending') },
                  ASSIGNED: { cls: 'assigned', label: t('cleaner.statusAssigned') },
                  COMPLETED: { cls: 'assigned', label: t('cleaner.statusCompleted') }
                }[task.status] || { cls: 'open', label: task.status };
                const isConfirmed = task.status === 'ASSIGNED';
                const now = nowTick;
                const inWindow = now >= startDate && now <= endDate;
                // Enkel binnen dit venster mag de taak effectief afgerond
                // worden (bevestigd + tijdens de voorziene uren).
                const withinConfirmedWindow = isConfirmed && inWindow;
                // Zodra de voorziene schoonmaakperiode zelf al begonnen is, mag
                // er niet meer afgemeld worden (zie "Afmelden"-knop verderop) -
                // dat zou de admin/verhuurder overvallen net wanneer de
                // schoonmaak eigenlijk al aan de gang zou moeten zijn. Zelfde
                // check als de backend ("/cancel-assignment") nu ook doet.
                const hasStarted = now >= startDate;
                const isClockBusy = clockActionTaskId === task.id;

                // Welke "slot" ben ik op deze taak (hoofd-schoonmaker of een
                // van de co-schoonmakers)? Elk heeft zijn eigen in-/uitklok-
                // tijdstip, zodat meerdere schoonmakers op dezelfde taak
                // onafhankelijk van elkaar kunnen in-/uitklokken.
                const mySlot = task.cleanerId === user.id ? '' : task.cleanerId2 === user.id ? '2' : task.cleanerId3 === user.id ? '3' : null;
                const myClockInAt = mySlot !== null ? task[`cleaner${mySlot}ClockInAt`] : null;
                const myClockOutAt = mySlot !== null ? task[`cleaner${mySlot}ClockOutAt`] : null;
                // Batterijstatus + instructies-PDF zijn enkel relevant "ter
                // plaatse" - dus pas zichtbaar zolang je effectief ingeklokt
                // bent voor deze taak. De batterijknop staat bovendien enkel
                // bij woningen met een toegangsbeheer-abonnement.
                const isClockedIn = mySlot !== null && !!myClockInAt && !myClockOutAt;
                const canShowBattery = isClockedIn && task.access_management_enabled;
                const canShowPdf = isClockedIn && task.cleaningPdfOriginalName;

                const expanded = expandedOpenTaskId === task.id;
                const canClockIn = mySlot !== null && !myClockInAt && withinConfirmedWindow;
                const canClockOut = mySlot !== null && !!myClockInAt && !myClockOutAt;

                return (
                  <div key={task.id} className={`compact-task ${expanded ? 'expanded' : ''} ${taskStripeClass(task, user.id)}`}>
                    <div
                      className="compact-task-summary"
                      role="button"
                      tabIndex={0}
                      aria-expanded={expanded}
                      onClick={() => toggleCompactTask(task.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCompactTask(task.id); } }}
                    >
                      {task.property_photo_url && <img src={`${API_URL}${task.property_photo_url}`} alt="" className="compact-task-photo" />}
                      <div className="compact-task-summary-body">
                      <div className="compact-task-line">
                        <span className="compact-task-name">
                          {task.property_name}
                          {/* Geen "Toegewezen"-label bij bevestigde taken. */}
                          {task.status !== 'ASSIGNED' && (
                            <span className="compact-task-status">{statusInfo.label}</span>
                          )}
                        </span>
                        <span className="compact-task-time">
                          {formatTimeOnly(task.scheduledAt)}{task.cleaningEnd ? ` - ${formatTimeOnly(task.cleaningEnd)}` : ''}
                        </span>
                      </div>
                      <div className="compact-task-line compact-task-meta">
                        <span>{t('cleaner.duration')}: {formatDurationMinutes(task.worked_minutes)}</span>
                        {renderSignupBadge(task)}
                      </div>
                      </div>
                      <span className="compact-task-chevron" aria-hidden="true" />
                    </div>

                    {/* In-/uitklokknop staat nooit zichtbaar buiten de
                        schoonmaakperiode: Inklokken verschijnt pas binnen de
                        periode; Uitklokken zodra je ingeklokt bent. */}
                    {(canClockIn || canClockOut) && (
                      <div className="compact-task-clock">
                        {canClockIn && (
                          <button
                            onClick={() => handleClockIn(task.id)}
                            className="ct-btn ct-btn-assign"
                            disabled={isClockBusy}
                          >
                            {t('cleaner.clockIn')}
                          </button>
                        )}
                        {canClockOut && (
                          <div className="clocked-in-line">{t('cleaner.clockedInAt', { time: formatTimeOnly(myClockInAt) })}</div>
                        )}
                        {canClockOut && (
                          <button
                            onClick={() => handleClockOut(task.id)}
                            className="ct-btn ct-btn-assign"
                            disabled={!withinConfirmedWindow || isClockBusy}
                          >
                            {t('cleaner.clockOut')}
                          </button>
                        )}
                      </div>
                    )}

                    {expanded && (
                      <div className="compact-task-details">
                        <p><strong>{t('cleaner.cleaningPeriod')}:</strong> {formatCleaningTimeRange(task)}</p>
                        <p><strong>{t('common.address')}:</strong> {task.address || t('cleaner.noAddress')}</p>
                        {(() => {
                          const people = [
                            { name: task.cleaner_name, photo: task.cleaner_photo_url },
                            { name: task.cleaner2_name, photo: task.cleaner2_photo_url },
                            { name: task.cleaner3_name, photo: task.cleaner3_photo_url },
                          ].filter((c) => c.name).sort((a, b) => compareNames(a.name, b.name));
                          return people.length > 0 && (
                            <div className="compact-task-people">
                              <strong>{t('cleaner.assignedTo')}:</strong>
                              {people.map((c) => (
                                <div key={c.name} className="compact-task-person">
                                  <Avatar url={c.photo} name={c.name} size={32} />
                                  <span>{c.name}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        {task.notes && <p><strong>{t('cleaner.notes')}:</strong> {task.notes}</p>}
                        {/* Algemene "Extra info" van de woning: enkel bij een
                            bevestigde (ASSIGNED) taak - de backend stuurt dit veld
                            anders niet mee. */}
                        {task.extra_info && (
                          <p style={{ whiteSpace: 'pre-line' }}><strong>{t('admin.extraInfoLabel')}:</strong> {task.extra_info}</p>
                        )}
                        {mySlot !== null && myClockInAt && (
                          <p>
                            <strong>{t('cleaner.workedTime')}:</strong>{' '}
                            {formatTimeOnly(myClockInAt)} - {myClockOutAt ? formatTimeOnly(myClockOutAt) : t('cleaner.clockedInOngoing')}
                          </p>
                        )}

                        {/* Afmelden kan enkel nog zolang ik kandidaat ben
                            (PENDING_CONFIRMATION) en de periode nog niet begonnen is. */}
                        {/* Binnen 2 weken voor de taak staat de knop er niet meer. */}
                        {['PENDING_CONFIRMATION', 'ASSIGNED'].includes(task.status) && mySlot === '' && !myClockInAt && !isCancelLocked(task, nowTick) && (
                          <div className="compact-task-actions">
                            <button
                              onClick={() => handleCancelAssignment(task.id)}
                              className="ct-btn ct-btn-cancel"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        )}

                        {(canShowPdf || canShowBattery) && (
                          <div className="task-onsite-tools">
                            {canShowPdf && (
                              <button onClick={() => handleViewCleaningPdf(task)} className="checklist-btn small">
                                {t('cleaner.viewCleaningPdf')}
                              </button>
                            )}
                            {canShowBattery && (
                              <button onClick={() => toggleSmartlockPanel(task)} className="checklist-btn small">
                                {smartlockPanelTaskId === task.id ? t('cleaner.hideBatteryStatus') : t('cleaner.viewBatteryStatus')}
                              </button>
                            )}
                          </div>
                        )}

                    {canShowBattery && smartlockPanelTaskId === task.id && (
                      <div className="cleaner-battery-panel">
                        {smartlockStatusLoading && (
                          <p className="battery-note">{t('common.loading')}</p>
                        )}
                        {!smartlockStatusLoading && (!smartlockStatus || !smartlockStatus.linked) && (
                          <p className="battery-note">{t('dashboard.noSmartlockLinked')}</p>
                        )}
                        {!smartlockStatusLoading && smartlockStatus?.linked && !smartlockStatus.configured && (
                          <p className="battery-note">{t('dashboard.smartlockNotConfigured')}</p>
                        )}
                        {!smartlockStatusLoading && smartlockStatus?.linked && smartlockStatus.configured && !smartlockStatus.reachable && (
                          <p className="battery-note">{t('dashboard.smartlockUnreachable')}</p>
                        )}
                        {!smartlockStatusLoading && smartlockStatus?.reachable && smartlockStatus.batteryCharge != null && (
                          <>
                            <div className="battery-level">
                              <div className="battery-bar">
                                <div
                                  className={`battery-fill ${smartlockStatus.batteryCharge <= 20 ? 'low' : smartlockStatus.batteryCharge <= 50 ? 'medium' : 'high'}`}
                                  style={{ width: `${smartlockStatus.batteryCharge}%` }}
                                />
                              </div>
                              <span className="battery-percentage">{smartlockStatus.batteryCharge}%</span>
                            </div>
                            {smartlockStatus.batteryCharging && (
                              <p className="battery-note battery-charging-note">{t('dashboard.batteryCharging')}</p>
                            )}
                            {!smartlockStatus.batteryCharging && (smartlockStatus.batteryCritical || smartlockStatus.batteryCharge <= 20) && (
                              <div className="battery-warning">{t('dashboard.batteryLow')}</div>
                            )}
                          </>
                        )}
                        {!smartlockStatusLoading && smartlockStatus?.reachable && (
                          <button
                            type="button"
                            className="smartlock-sync-btn"
                            onClick={() => handleSyncSmartlock(task.propertyId)}
                            disabled={smartlockSyncing}
                          >
                            {smartlockSyncing ? t('dashboard.smartlockSyncing') : t('dashboard.smartlockSyncButton')}
                          </button>
                        )}
                      </div>
                    )}
                      </div>
                    )}
                  </div>
                );
                })}
                </div>
              </div>
              ))
            )}
          </div>
        )}

          </div>
        )}

        {!loading && activeTab === 'my-code' && (
          <div className="code-section">
            <div className="code-card-large">
              <div className="code-header-large">
                <h3>{t('cleaner.myCodeTitle')}</h3>
                <p className="code-description">
                  {t('cleaner.myCodeDescription')}
                </p>
              </div>
              <div className="code-display">
                <div className="code-value-large">{personalCode}</div>
                <button onClick={() => setShowCodeModal(true)} className="change-code-btn-large">
                  {t('cleaner.changeCode')}
                </button>
              </div>
              <div className="code-warning">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12M12 16H12.01M3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span>{t('cleaner.codeWarning')}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal voor code wijzigen */}
      {showCodeModal && (
        <div className="modal-overlay" onClick={() => setShowCodeModal(false)}>
          <div className="modal code-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('cleaner.changeCodeTitle')}</h2>
              <button className="modal-close" onClick={() => setShowCodeModal(false)}>✖</button>
            </div>
            <div className="modal-body">
              <div className="warning-box">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M12 8V12M12 16H12.01M3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span>{t('cleaner.codeWarningText')}</span>
              </div>
              <div className="form-group">
                <label>{t('cleaner.newCode6')}</label>
                <input
                  type="text"
                  value={newPersonalCode}
                  onChange={(e) => setNewPersonalCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
              </div>
              <div className="form-group">
                <label>{t('cleaner.repeatCode')}</label>
                <input
                  type="text"
                  value={confirmPersonalCode}
                  onChange={(e) => setConfirmPersonalCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
              </div>
              {codeError && <div className="error-message">{codeError}</div>}
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowCodeModal(false)} className="cancel-btn">{t('common.cancel')}</button>
              <button onClick={handleChangePersonalCode} className="submit-btn">{t('cleaner.changeCode')}</button>
            </div>
          </div>
        </div>
      )}

      {isNative && createPortal(
        <nav className="native-tabbar">
          <button type="button" className={`native-tab ${activeTab === 'calendar' ? 'active' : ''}`} aria-label={t('cleaner.tabCalendar')} onClick={() => setActiveTab('calendar')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 10H21M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
          <button type="button" className={`native-tab ${activeTab === 'available' ? 'active' : ''}`} aria-label={t('cleaner.tabAvailable', { count: openTasks.length })} onClick={() => setActiveTab('available')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6h11M9 12h11M9 18h11"/><path d="M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2"/></svg>
            {signUpCount > 0 && <span className="native-tab-badge native-tab-badge-blue">{signUpCount > 99 ? '99+' : signUpCount}</span>}
          </button>
          <button type="button" className={`native-tab ${(activeTab === 'my-tasks' || activeTab === 'reserves') ? 'active' : ''}`} aria-label={t('cleaner.tabMyTasksShort')} onClick={() => setActiveTab('my-tasks')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 11L12 3L21 11M5 9.222V20H9.5V14H14.5V20H19V9.222" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
          </button>
          <button type="button" className={`native-tab ${activeTab === 'notifications' ? 'active' : ''}`} aria-label={t('notifications.title')} onClick={() => setActiveTab('notifications')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
            {nativeUnread > 0 && <span className="native-tab-badge">{nativeUnread > 99 ? '99+' : nativeUnread}</span>}
          </button>
          <button type="button" className={`native-tab ${activeTab === 'profile' ? 'active' : ''}`} aria-label={t('profileMenu.title')} onClick={() => setActiveTab('profile')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
          </button>
        </nav>,
        document.body
      )}
    </div>
  );
}

export default CleanerDashboard;
